import { useState, useEffect, useCallback } from 'react'
import { useParams, useSearchParams, Link } from 'react-router'
import { ArrowLeft } from 'lucide-react'

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { useApiClient } from '@/shared/api'
import { useCampaign } from '@/entities/campaign'

import { CallsTable, type StatusCallResult } from './ui/CallsTable'

interface CallsData {
  calls: StatusCallResult[]
  total: number
}

function LoadingSkeleton({ tab }: { tab: string }) {
  const columnCount = tab === 'queue' ? 5 : 9
  return (
    <div className="space-y-2">
      {Array.from({ length: 10 }).map((_, i) => (
        <div key={i} className="flex gap-4">
          {Array.from({ length: columnCount }).map((_, j) => (
            <Skeleton key={j} className="h-8 flex-1" />
          ))}
        </div>
      ))}
    </div>
  )
}

export function DialerStatus() {
  const { campaignId: campaignIdParam } = useParams<{ campaignId: string }>()
  const campaignId = parseInt(campaignIdParam || '0')
  const apiClient = useApiClient()
  const [searchParams, setSearchParams] = useSearchParams()

  const { data: campaign, isLoading: campaignLoading } = useCampaign(campaignId)

  const [callsData, setCallsData] = useState<CallsData | null>(null)
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const activeTab = searchParams.get('tab') || 'last'
  const offset = parseInt(searchParams.get('offset') || '0')
  const limit = 200

  const fetchCalls = useCallback(
    async (isInitial = false) => {
      if (!campaignId) return

      if (isInitial) {
        setIsInitialLoading(true)
      } else {
        setIsRefreshing(true)
      }
      setError(null)

      try {
        const params = new URLSearchParams({
          campaign_id: campaignId.toString(),
          tab: activeTab,
          offset: offset.toString(),
          limit: limit.toString(),
        })

        const result = await apiClient<CallsData>(`/dialer/campaign/status/calls?${params}`)

        setCallsData(result)
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'An error occurred'
        setError(message)
      } finally {
        setIsInitialLoading(false)
        setIsRefreshing(false)
      }
    },
    [campaignId, apiClient, activeTab, offset, limit]
  )

  // Fetch calls when tab or offset changes
  useEffect(() => {
    fetchCalls(true)
  }, [activeTab, offset, fetchCalls])

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh) return

    const intervalId = setInterval(() => {
      fetchCalls(false)
    }, 5000)

    return () => clearInterval(intervalId)
  }, [autoRefresh, fetchCalls])

  if (campaignLoading) {
    return (
      <div className="w-full h-full p-6 overflow-y-auto">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (!campaign) {
    return (
      <div className="w-full h-full p-6 overflow-y-auto">
        <Alert variant="destructive">
          <AlertTitle>Campaign Not Found</AlertTitle>
          <AlertDescription>The requested campaign could not be found.</AlertDescription>
        </Alert>
      </div>
    )
  }

  const handleTabChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('tab', value)
    newParams.delete('offset') // Reset to first page
    setSearchParams(newParams)
  }

  const handlePrevPage = () => {
    const newOffset = Math.max(0, offset - limit)
    const newParams = new URLSearchParams(searchParams)
    newParams.set('offset', newOffset.toString())
    setSearchParams(newParams)
  }

  const handleNextPage = () => {
    const newOffset = offset + limit
    const newParams = new URLSearchParams(searchParams)
    newParams.set('offset', newOffset.toString())
    setSearchParams(newParams)
  }

  const hasNextPage = callsData ? offset + limit < callsData.total : false
  const hasPrevPage = offset > 0

  return (
    <div className="w-full h-full p-6 overflow-y-auto">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <Link to="/dialer">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-heading font-bold text-gray-900">{campaign.name}</h1>
            <p className="text-body text-gray-600">Campaign Status</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Call Status</CardTitle>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="autoRefresh"
                  checked={autoRefresh}
                  onCheckedChange={(checked) => setAutoRefresh(!!checked)}
                />
                <Label htmlFor="autoRefresh" className="font-medium cursor-pointer">
                  Auto refresh (5s)
                </Label>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <TabsList className="grid w-full grid-cols-5 mb-4">
                <TabsTrigger value="last">Last Calls</TabsTrigger>
                <TabsTrigger value="na">NA</TabsTrigger>
                <TabsTrigger value="progress">In Progress</TabsTrigger>
                <TabsTrigger value="transfers">Transfers</TabsTrigger>
                <TabsTrigger value="queue">Top Queue</TabsTrigger>
              </TabsList>

              {['last', 'na', 'progress', 'transfers', 'queue'].map((tab) => (
                <TabsContent key={tab} value={tab} className="space-y-4">
                  {isInitialLoading ? (
                    <LoadingSkeleton tab={tab} />
                  ) : error ? (
                    <div className="text-center text-red-600 py-8">{error}</div>
                  ) : callsData ? (
                    <>
                      {isRefreshing && (
                        <div className="text-detail text-muted-foreground text-right mb-2">
                          Refreshing...
                        </div>
                      )}
                      <CallsTable calls={callsData.calls} tab={tab} />
                    </>
                  ) : null}
                </TabsContent>
              ))}
            </Tabs>

            {/* Pagination */}
            {callsData && callsData.total > 0 && (
              <div className="mt-4 flex items-center justify-between">
                <div className="text-body text-muted-foreground">
                  Showing {offset + 1} to {Math.min(offset + limit, callsData.total)} of{' '}
                  {callsData.total} calls
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrevPage}
                    disabled={!hasPrevPage || isInitialLoading}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={!hasNextPage || isInitialLoading}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}